// Naturals demo POS API client. Talks only to REACT_APP_DEMO_POS_API_URL.
function defaultPosApiBase() {
  if (typeof window === 'undefined') return 'http://localhost:8012';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'http://localhost:8012';
  }
  return `${window.location.origin}/demo-pos-api`;
}

const BASE = (process.env.REACT_APP_DEMO_POS_API_URL || defaultPosApiBase()).replace(/\/+$/, '');

export const SALON_NAME = process.env.REACT_APP_DEMO_ENGAGEMENT_SALON_NAME || 'Naturals';

/** Frontend needs at least this backend API version (see backend app/__init__.py). */
export const REQUIRED_API_VERSION = 2;

function query(params = {}) {
  const clean = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return clean.length ? `?${new URLSearchParams(clean)}` : '';
}

const TIMEOUT_MS = 30000;

async function request(path, { method = 'GET', body, form } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const init = { method, headers: {}, signal: controller.signal };
  if (form) {
    init.body = form;
  } else if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  let res;
  try {
    res = await fetch(`${BASE}${path}`, init);
  } catch (e) {
    throw new Error(e.name === 'AbortError' ? 'The POS server took too long to answer. Try again.' : 'Cannot reach the POS server.');
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail = data && typeof data === 'object' ? data.detail || data.error : data;
    const err = new Error(typeof detail === 'string' && detail ? detail : `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data && typeof data === 'object' ? data.code : undefined;
    throw err;
  }
  return data;
}

const bill = (id) => `/api/v1/bills/${encodeURIComponent(id)}`;

const api = {
  health: () => request('/health'),
  meta: () => request('/api/v1/meta'),

  branches: () => request('/api/v1/branches').then((d) => d?.items || []),
  services: (franchiseId) =>
    request(`/api/v1/catalog/services${query({ franchise_id: franchiseId })}`).then((d) => d?.items || []),
  stylists: () => request('/api/v1/catalog/stylists').then((d) => d?.items || []),

  lookupCustomer: (phone) => request(`/api/v1/customers/lookup${query({ phone })}`),

  queue: (branches) => request(`/api/v1/queue${query({ branches })}`),
  billFromQueue: (payload) => request('/api/v1/queue/bill', { method: 'POST', body: payload }),

  bills: (params) => request(`/api/v1/bills${query(params)}`).then((d) => d?.items || []),
  createBill: (payload) => request('/api/v1/bills', { method: 'POST', body: payload }),
  bill: (id) => request(bill(id)),
  identify: (id, payload) => request(`${bill(id)}/identify`, { method: 'POST', body: payload }),
  identifyFace: (id, blob) => {
    const form = new FormData();
    form.append('image', blob, 'face.jpg');
    return request(`${bill(id)}/identify`, { method: 'POST', form });
  },
  setItems: (id, payload) => request(`${bill(id)}/items`, { method: 'PATCH', body: payload }),
  setBranch: (id, payload) => request(`${bill(id)}/branch`, { method: 'PATCH', body: payload }),
  pay: (id, payload) => request(`${bill(id)}/payments`, { method: 'POST', body: payload }),
  verifyUpi: (id, paymentId, payload) =>
    request(`${bill(id)}/payments/${encodeURIComponent(paymentId)}/verify`, { method: 'POST', body: payload }),
  confirmManager: (id, payload) => request(`${bill(id)}/confirm-manager`, { method: 'POST', body: payload }),
  approve: (id, payload) => request(`${bill(id)}/approve`, { method: 'POST', body: payload || {} }),
  finalize: (id) => request(`${bill(id)}/finalize`, { method: 'POST', body: {} }),
  deliver: (id, payload) => request(`${bill(id)}/deliver`, { method: 'POST', body: payload }),

  whatsappStatus: () => request('/api/v1/whatsapp/status'),

  analytics: (params) => request(`/api/v1/analytics${query(params)}`),
};

export default api;
